import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  MouseEvent,
} from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import BrowserUpdatedIcon from "@mui/icons-material/BrowserUpdated";
import Form from "react-bootstrap/Form";
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_DensityState,
  type MRT_Row,
  MRT_SortingState,
  MRT_VisibilityState,
  useMaterialReactTable,
  type MRT_TableOptions,
} from "material-react-table";
import HistoryIcon from "@mui/icons-material/History";
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Autocomplete,
  TextField,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Accordion from "react-bootstrap/Accordion";
import { Container, Row } from "reactstrap";
import axiosInstance from "../../Utils/axiosInstance";
import { backendbaseUrl } from "../../Utils/baseUrl";
import { toast } from "react-toastify";
import styled from "styled-components";
import { showDeleteConfirmModal } from "../../Components/ConfirmModal";
import { CompactTextField } from "../../Components/SharedComponents/SharedComponents";
import FileUpload from "../../Components/FileUpload/FileUpload";
import BrokerTemplateDownloadButton from "../../Components/TemplateDownloadButton/BrokerTemplateDownloadButton";
import { useNavigate } from "react-router-dom";
import { BookOption, ProfitShare } from "../../Types/ProfitShareByBook.type";


export const StyledTableFirstSecond = styled.div`
  thead tr:first-child th:first-child { width: 4% !important; }
  thead tr:first-child th:nth-child(2) { width: 1% !important; }
`;


const ProfitShareExample = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
  const [formErrors, setFormErrors] = useState<Record<string, string>>({
    book: "", profit_share_percentage: "", effective_from: "", effective_to: "",
  });
  const [touched, setTouched] = useState<Record<string, boolean>>({
    book: false, profit_share_percentage: false, effective_from: false, effective_to: false,
  });

  const [selectedBook, setSelectedBook] = useState<BookOption | null>(null);
  const [profitSharePercentage, setProfitSharePercentage] = useState<string>("");
  const [effectiveFrom, setEffectiveFrom] = useState<string>("");
  const [effectiveTo, setEffectiveTo] = useState<string>("");
  const [active, setActive] = useState<boolean>(true);
  const [inputBackgroundColour, setInputBackgroundColour] = useState("");

  const [rowSave, setRowSave] = useState<Record<string, boolean>>({});
  const markRowSave = (rowId: string) => {
    setRowSave((prev) => ({ ...prev, [rowId]: true }));
  };

  const isFirstRender = useRef(true);
  const [filterModes, setFilterModes] = useState<{ mode: string; id: string; value: unknown }[]>([]);
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>({});
  const [density, setDensity] = useState<MRT_DensityState>("comfortable");
  const [globalFilter, setGlobalFilter] = useState<string | undefined>(undefined);
  const [showGlobalFilter, setShowGlobalFilter] = useState(true);
  const [showColumnFilters, setShowColumnFilters] = useState(true);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 15 });
  const [totalCount, setTotalCount] = useState(0);
  const [showRowResizing, setShowRowResizing] = useState<boolean>(true);
  const [openFileUpload, setOpenFileUpload] = useState<boolean>(false);


  const handleFileUploadOpen = () => setOpenFileUpload(true);
  const handleFileUploadClose = () => setOpenFileUpload(false);

  const { data: bookOptions = [] } = useQuery<BookOption[]>({
    queryKey: ["bookDropdown"],
    queryFn: async () => {
      const response = await axiosInstance.get(`${backendbaseUrl}/paper-app/legal-entity-book-dropdown/`);
      return response.data as BookOption[];
    },
    refetchOnWindowFocus: false,
  });

  const handleFileUploadSubmit = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axiosInstance.post(
        `${backendbaseUrl}/paper-app/upload/profit-share/`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      queryClient.invalidateQueries({ queryKey: ["profitShares"] });
      return { success: true, message: "File uploaded successfully!", data: response.data };
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || "Failed to upload file" };
    }
  };
  const validateField = (field: string, value: string): string | null => {
    if (field === "book" && !value) return "Book is Required";

    if (field === "profit_share_percentage") {
      if (!value && value !== "0") return "Profit Share % is Required";
      if (!/^-?\d*\.?\d+$/.test(value)) return "Must be a numeric value (e.g. 25.00)";
      const num = parseFloat(value);
      if (isNaN(num)) return "Must be a numeric value (e.g. 25.00)";
      if (num < 0) return "Value must be ≥ 0";
      if (num > 100) return "Value must be ≤ 100";
    }

    if (field === "effective_from" && !value) return "Effective From is Required";
    return null;
  };

  const handleFocus = (field: string) => setTouched((prev) => ({ ...prev, [field]: true }));

  const validateOnBlur = (field: string) => (value: string): string | null => {
    const error = validateField(field, value);
    setFormErrors((prev) => ({ ...prev, [field]: error || "" }));
    return error;
  };

  const validateEffectiveDates = (
    bookId: string,
    newFrom: string,
    newTo: string | null,
    excludeId?: string
  ): string | null => {
    if (newTo && newFrom && newTo < newFrom) {
      return 'Effective To must be >= Effective From';
    }
    const existing = (data?.results || []).filter(
      (r) => r.book === bookId && r.active === true && r.id !== excludeId
    );
    for (const rec of existing) {
      const recFrom = rec.effective_from;
      const recTo = rec.effective_to;
      if (!newTo && !recTo) {
        return 'An open-ended active record already exists for this book (from ' + recFrom + '). Please set an Effective To date before adding another.';
      }
      const newEnd = newTo ?? '9999-12-31';
      const recEnd = recTo ?? '9999-12-31';
      if (newFrom <= recEnd && recFrom <= newEnd) {
        const recToLabel = recTo ? recTo : 'open-ended';
        return 'Date range overlaps with existing active record (' + recFrom + ' to ' + recToLabel + ') for this book.';
      }
    }
    return null;
  };

  const handleProfitShareChange = (value: string) => {
    setProfitSharePercentage(value);
    if (touched.profit_share_percentage) {
      const error = validateField("profit_share_percentage", value);
      setFormErrors((prev) => ({ ...prev, profit_share_percentage: error || "" }));
    }
  };

  useEffect(() => {
    const cf = localStorage.getItem("mrt_columnFilters_profitshare_1");
    const cv = localStorage.getItem("mrt_columnVisibility_profitshare_1");
    const d = localStorage.getItem("mrt_density_profitshare_1");
    const gf = localStorage.getItem("mrt_globalFilter_profitshare_1");
    const sgf = localStorage.getItem("mrt_showGlobalFilter_profitshare_1");
    const scf = localStorage.getItem("mrt_showColumnFilters_profitshare_1");
    const s = localStorage.getItem("mrt_sorting_profitshare_1");
    if (cf) setColumnFilters(JSON.parse(cf));
    if (cv) setColumnVisibility(JSON.parse(cv));
    if (d) setDensity(JSON.parse(d));
    if (gf) setGlobalFilter(JSON.parse(gf) || undefined);
    if (sgf) setShowGlobalFilter(JSON.parse(sgf));
    if (scf) setShowColumnFilters(JSON.parse(scf));
    if (s) setSorting(JSON.parse(s));
    isFirstRender.current = false;
  }, []);

  useEffect(() => { if (!isFirstRender.current) localStorage.setItem("mrt_columnFilters_profitshare_1", JSON.stringify(columnFilters)); }, [columnFilters]);
  useEffect(() => { if (!isFirstRender.current) localStorage.setItem("mrt_columnVisibility_profitshare_1", JSON.stringify(columnVisibility)); }, [columnVisibility]);
  useEffect(() => { if (!isFirstRender.current) localStorage.setItem("mrt_density_profitshare_1", JSON.stringify(density)); }, [density]);
  useEffect(() => { if (!isFirstRender.current) localStorage.setItem("mrt_globalFilter_profitshare_1", JSON.stringify(globalFilter ?? "")); }, [globalFilter]);
  useEffect(() => { if (!isFirstRender.current) localStorage.setItem("mrt_showGlobalFilter_profitshare_1", JSON.stringify(showGlobalFilter)); }, [showGlobalFilter]);
  useEffect(() => { if (!isFirstRender.current) localStorage.setItem("mrt_showColumnFilters_profitshare_1", JSON.stringify(showColumnFilters)); }, [showColumnFilters]);
  useEffect(() => { if (!isFirstRender.current) localStorage.setItem("mrt_sorting_profitshare_1", JSON.stringify(sorting)); }, [sorting]);

  const formatDate = (val: string | null | undefined): string => {
    if (!val) return "—";
    const [year, month, day] = val.split("-").map(Number);
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${String(day).padStart(2, "0")}-${months[month - 1]}-${year}`;
  };

  const columns = useMemo<MRT_ColumnDef<ProfitShare>[]>(
    () => [
      {
        accessorKey: "book_name",
        header: "Book",
        enableEditing: true,
        Cell: ({ cell }) => <span>{cell.getValue<string>()}</span>,
        muiEditTextFieldProps: ({ row, table }) => {
          const currentBookId: string =
            row._valuesCache?.book !== undefined
              ? String(row._valuesCache.book)
              : String(row.original.book ?? "");

          return {
            select: true,
            value: currentBookId,
            error: !!validationErrors?.book,
            helperText: validationErrors?.book,
            onFocus: () =>
              setValidationErrors((prev) => ({ ...prev, book: undefined })),
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              const newId = e.target.value;
              const selectedBook = bookOptions.find(
                (b) => String(b.id) === String(newId)
              );
              if (String(newId) !== String(row.original.book)) {
                markRowSave(row.id);
              }
              row._valuesCache = {
                ...row._valuesCache,
                book: newId,
                book_name: selectedBook?.name ?? "",
              };
              table.setEditingRow({ ...row });
            },
            children: bookOptions.map((book) => (
              <MenuItem key={book.id} value={String(book.id)}>
                {book.name}
              </MenuItem>
            )),
          };
        },
        muiTableBodyCellProps: ({ row, table }) => ({
          sx: {
            cursor: "pointer",
            "&:hover": { backgroundColor: "rgba(44,4,4,0.05)" },
            ...(table.getState().editingRow?.id === row.id && {
              backgroundColor: "rgba(0,128,0,0.1)",
              transition: "background-color 0.3s ease",
            }),
          },
        }),
      },
      {
        accessorKey: "profit_share_percentage",
        header: "Profit Share %",
        enableEditing: true,
        muiTableBodyCellProps: ({ row, table }) => ({
          onDoubleClick: () => table.setEditingRow(row),
          sx: {
            cursor: "pointer",
            "&:hover": { backgroundColor: "rgba(44,4,4,0.05)" },
            ...(table.getState().editingRow?.id === row.id && {
              backgroundColor: "rgba(0,128,0,0.1)",
              transition: "background-color 0.3s ease",
            }),
          },
        }),
        muiEditTextFieldProps: ({ row }) => ({
          name: "profit_share_percentage",
          required: true,
          type: "number",
          inputProps: { min: 0, max: 100, step: 0.01 },
          error: !!validationErrors?.profit_share_percentage,
          helperText: validationErrors?.profit_share_percentage,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            if (String(e.target.value) !== String(row.original.profit_share_percentage)) {
              markRowSave(row.id);
            }
          },
          onFocus: () => setValidationErrors((prev) => ({ ...prev, profit_share_percentage: undefined })),
        }),
      },
      {
        accessorKey: "effective_from",
        header: "Effective From",
        enableEditing: true,
        Cell: ({ cell }) => formatDate(cell.getValue<string | null>()),
        muiTableBodyCellProps: ({ row, table }) => ({
          onDoubleClick: () => table.setEditingRow(row),
          sx: { cursor: "pointer", "&:hover": { backgroundColor: "rgba(44,4,4,0.05)" } },
        }),
        muiEditTextFieldProps: ({ row }) => ({
          name: "effective_from",
          required: true,
          type: "date",
          error: !!validationErrors?.effective_from,
          helperText: validationErrors?.effective_from,
          InputLabelProps: { shrink: true },
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.value !== (row.original.effective_from || "")) {
              markRowSave(row.id);
            }
          },
          onFocus: () => setValidationErrors((prev) => ({ ...prev, effective_from: undefined })),
        }),
      },
      {
        accessorKey: "effective_to",
        header: "Effective To",
        enableEditing: true,
        Cell: ({ cell }) => {
          const val = cell.getValue<string | null>();
          if (!val) {
            return (
              <Tooltip title="Open Ended" placement="top" arrow>
                <span style={{ cursor: "default" }}>—</span>
              </Tooltip>
            );
          }
          return <>{formatDate(val)}</>;
        },
        muiTableBodyCellProps: ({ row, table }) => ({
          onDoubleClick: () => table.setEditingRow(row),
          sx: { cursor: "pointer", "&:hover": { backgroundColor: "rgba(44,4,4,0.05)" } },
        }),
        muiEditTextFieldProps: ({ row }) => ({
          name: "effective_to",
          type: "date",
          InputLabelProps: { shrink: true },
          error: !!validationErrors?.effective_to,
          helperText: validationErrors?.effective_to,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.value !== (row.original.effective_to || "")) {
              markRowSave(row.id);
            }
          },
          onFocus: () => setValidationErrors((prev) => ({ ...prev, effective_to: undefined })),
        }),
      },
      {
        accessorKey: "active",
        header: "Active",
        size: 100,
        enableEditing: true,
        Cell: ({ cell }) => (
          <span style={{ color: cell.getValue<boolean>() ? "#2e7d32" : "#c62828", fontWeight: 600 }}>
            {cell.getValue<boolean>() ? "Yes" : "No"}
          </span>
        ),
        Edit: ({ row, table }) => {
          const currentVal =
            row._valuesCache?.active !== undefined
              ? row._valuesCache.active
              : row.original.active;
          return (
            <Checkbox
              checked={Boolean(currentVal)}
              onChange={(e) => {
                if (e.target.checked !== row.original.active) {
                  markRowSave(row.id);
                }
                row._valuesCache = { ...row._valuesCache, active: e.target.checked };
                table.setEditingRow({ ...row });
              }}
              size="small"
              sx={{
                color: "#b9baeb",
                "&.Mui-checked": { color: "#009db5" },
                padding: "4px",
              }}
            />
          );
        },
        muiTableBodyCellProps: ({ row, table }) => ({
          onDoubleClick: () => table.setEditingRow(row),
          sx: { cursor: "pointer", "&:hover": { backgroundColor: "rgba(44,4,4,0.05)" } },
        }),
      },
      {
        accessorKey: "last_updated_by",
        header: "Last Updated By",
        enableEditing: false,
      },
      {
        accessorKey: "updated_at",
        header: "Last Updated Date",
        enableEditing: false,
        Cell: ({ cell }) => {
          const val = cell.getValue<string | null>();
          if (!val) return "—";
          const datePart = val.split("T")[0];
          const [year, month, day] = datePart.split("-").map(Number);
          const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
          return `${String(day).padStart(2, "0")}-${months[month - 1]}-${year}`;
        },
      },
    ],
    [validationErrors, rowSave, bookOptions]
  );

  const { data, isError: isLoadingError, isFetching, isLoading } =
    useGetProfitShares(filterModes, pagination, setTotalCount, sorting);

  const { mutateAsync: createProfitShare, isPending: isCreating } = useCreateProfitShare();
  const { mutateAsync: updateProfitShare, isPending: isUpdating } = useUpdateProfitShare();
  const { mutateAsync: deleteProfitShare, isPending: isDeleting } = useDeleteProfitShare();

  const createRecord = async (payload: {
    book: string;
    profit_share_percentage: number;
    effective_from: string;
    effective_to: string | null;
    active: boolean;
  }) => {
    try {
      await axiosInstance.post(`${backendbaseUrl}/paper-app/legal-entity-profit-share/`, payload);
      queryClient.invalidateQueries({ queryKey: ["profitShares"] });
      toast.success("Profit Share added successfully!");
    } catch (error: any) {
      const msg =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Failed to add Profit Share.";
      toast.error(msg);
      throw error;
    }
  };

  const formCreate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setTouched({ book: true, profit_share_percentage: true, effective_from: true, effective_to: true });

    const bookError = validateField("book", selectedBook?.id || "");
    const pctError = validateField("profit_share_percentage", profitSharePercentage);
    const fromError = validateField("effective_from", effectiveFrom);

    const dateRuleError = !bookError && !fromError && selectedBook
      ? validateEffectiveDates(selectedBook.id, effectiveFrom, effectiveTo || null)
      : null;

    if (bookError || pctError || fromError || dateRuleError) {
      setFormErrors({
        book: bookError || "",
        profit_share_percentage: pctError || "",
        effective_from: fromError || dateRuleError || "",
        effective_to: dateRuleError && !fromError ? dateRuleError : "",
      });
      const firstError = bookError || pctError || fromError || dateRuleError;
      toast.error(firstError || "Please fix validation errors before submitting.");
      return;
    }

    await createRecord({
      book: selectedBook!.id,
      profit_share_percentage: parseFloat(profitSharePercentage),
      effective_from: effectiveFrom,
      effective_to: effectiveTo || null,
      active,
    });

    setSelectedBook(null);
    setProfitSharePercentage("");
    setEffectiveFrom("");
    setEffectiveTo("");
    setActive(true);
    setTouched({ book: false, profit_share_percentage: false, effective_from: false, effective_to: false });
    setFormErrors({ book: "", profit_share_percentage: "", effective_from: "", effective_to: "" });
  };

  const handleCreateRow: MRT_TableOptions<ProfitShare>["onCreatingRowSave"] = async ({ values, table }) => {
    const errors: Record<string, string | undefined> = {};
    if (!values.book) errors.book = "Book is Required";

    const pctErr = validateField("profit_share_percentage", String(values.profit_share_percentage ?? ""));
    if (pctErr) errors.profit_share_percentage = pctErr;

    if (!values.effective_from) errors.effective_from = "Effective From is Required";
    if (Object.values(errors).some(Boolean)) { setValidationErrors(errors); return; }
    setValidationErrors({});
    await createRecord({
      book: values.book,
      profit_share_percentage: parseFloat(values.profit_share_percentage),
      effective_from: values.effective_from,
      effective_to: values.effective_to || null,
      active: values.active ?? true,
    });
    table.setCreatingRow(null);
  };

  const handleEditSave: MRT_TableOptions<ProfitShare>["onEditingRowSave"] = async ({ values, table, row }) => {
    const bookId: string =
      row._valuesCache?.book !== undefined
        ? String(row._valuesCache.book)
        : String(values.book ?? row.original.book);

    const merged: ProfitShare = {
      ...row.original,
      ...values,
      book: bookId,
    };

    if (!bookId) {
      setValidationErrors({ book: "Book is Required" });
      toast.error("Book is Required");
      return;
    }

    const pctError = validateField("profit_share_percentage", String(merged.profit_share_percentage));
    if (pctError) {
      setValidationErrors({ profit_share_percentage: pctError });
      toast.error(pctError);
      return;
    }

    if (!merged.effective_from) {
      toast.error("Effective From is Required");
      return;
    }

    const dateRuleError = validateEffectiveDates(
      bookId,
      merged.effective_from,
      merged.effective_to || null,
      merged.id
    );
    if (dateRuleError) {
      setValidationErrors({ effective_from: dateRuleError });
      toast.error(dateRuleError);
      return;
    }

    setValidationErrors({});
    try {
      await updateProfitShare({
        id: merged.id,
        book: bookId,
        profit_share_percentage: parseFloat(String(merged.profit_share_percentage)),
        effective_from: merged.effective_from,
        effective_to: merged.effective_to || null,
        active: merged.active ?? true,
      });
      table.setEditingRow(null);
      toast.success("Profit Share updated successfully");

      setRowSave((prev) => {
        const next = { ...prev };
        delete next[merged.id];
        return next;
      });
    } catch {
      toast.error("Failed to update Profit Share");
    }
  };

  const openDeleteConfirmModal = (row: MRT_Row<ProfitShare>) => {
    showDeleteConfirmModal({
      title: "Are you sure you want to delete this Profit Share record?",
      content: "Deleting...",
      onOk: () => {
        deleteProfitShare(row.original.id)
          .then(() => toast.success("Profit Share deleted successfully!"))
          .catch(() => toast.error("Failed to delete Profit Share. Please try again."));
      },
    });
  };

  const handleCopyRow = (row: MRT_Row<ProfitShare>) => {
    if (!row) return;
    const textToCopy = JSON.stringify(row.original, null, 2);
    navigator.clipboard.writeText(textToCopy);
    try {
      const jsonData = JSON.parse(textToCopy) as ProfitShare;
      setSelectedBook(bookOptions.find((b) => b.id === jsonData.book) || null);
      setProfitSharePercentage(String(jsonData.profit_share_percentage));
      setEffectiveFrom(jsonData.effective_from);
      setEffectiveTo(jsonData.effective_to || "");
      setActive(jsonData.active ?? true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Copied and pasted successfully");
      setInputBackgroundColour("#83bf6e26");
      setTimeout(() => setInputBackgroundColour(""), 2400);
    } catch (error) { console.error("Invalid JSON:", error); }
  };

  interface ContextMenuPosition { mouseX: number; mouseY: number }
  const [contextMenu, setContextMenu] = useState<ContextMenuPosition | null>(null);

  const handleContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setContextMenu(contextMenu === null ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 } : null);
  };
  const handleClose = () => setContextMenu(null);

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const jsonData = JSON.parse(text) as ProfitShare;
      setSelectedBook(bookOptions.find((b) => b.id === jsonData.book) || null);
      setProfitSharePercentage(String(jsonData.profit_share_percentage));
      setEffectiveFrom(jsonData.effective_from);
      setEffectiveTo(jsonData.effective_to || "");
      setActive(jsonData.active ?? true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Copied and pasted successfully");
      setInputBackgroundColour("#83bf6e26");
      setTimeout(() => setInputBackgroundColour(""), 2400);
    } catch (err) { console.error("Failed to read clipboard:", err); }
    handleClose();
  };

  const handleResizing = () => { setShowRowResizing(!showRowResizing); handleClose(); };

  const handleExportRows = async (rows: MRT_Row<any>[]) => {
      if (rows.length === 0) {
        toast.warn("Please select at least one row to export");
        return;
      }
  
    try {
      const rowIds = rows.map((row) => row.original.id);
      const response = await axiosInstance.post(
        `${backendbaseUrl}/paper-app/legal-entity-profit-share/export-selected/`,
        { ids: rowIds },
          {
            responseType: "blob",
          }
      );
  
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "selected_profit_shares.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  
      toast.success("Export started successfully");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export selected rows");
    }
  };
  

  const handleDataDownload = async () => {
    try {
      const response = await axiosInstance.get(
        `${backendbaseUrl}/paper-app/export-legal-entity-profit-share/`,
        { responseType: "blob", headers: { "Content-Type": "application/json" } }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "all_profit_shares.csv");
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 100);
      toast.success("All Data Exported Successfully");
    } catch { toast.error("Failed to download data"); }
  };

  const handleBulkDelete = async (rows: MRT_Row<ProfitShare>[]) => {
    const rowIds = rows.map((r) => r.original.id);
    if (!rowIds.length) {
      toast.warn("Please select at least one record to delete.");
      return;
    }
    showDeleteConfirmModal({
      title: `Delete ${rowIds.length} selected record(s)?`,
      content: `This will permanently delete ${rowIds.length} record(s). Continue?`,
      onOk: async () => {
        const loadingToast = toast.loading(
          `Deleting ${rowIds.length} record(s)...`
        );
        try {
          const response = await axiosInstance.post(
            `${backendbaseUrl}/paper-app/legal-entity-profit-share/delete-selected/`,
            { ids: rowIds }
          );
          if (response.status === 200) {
            toast.success(`Successfully deleted ${rowIds.length} record(s)`);
            queryClient.invalidateQueries({ queryKey: ["profitShares"] });
            table.setRowSelection({});
          } else {
            throw new Error(response.data?.message || "Unknown error");
          }
        } catch (error: any) {
          toast.error(
            `Deletion failed: ${
              error.response?.data?.detail || error.message || "Failed"
            }`
          );
        } finally {
          toast.dismiss(loadingToast);
        }
      },
    });
  };

  const handleHistory = () => navigate("/settings/log/history/profit-share");
  const openRowHistory = (row: MRT_Row<ProfitShare>) =>
    navigate(`/settings/log/history/profit-share/${row.original.id}`);

  useEffect(() => {
    setFilterModes(
      columnFilters.map((filter) => ({
        ...filter,
        mode: table.getColumn(filter.id)?.getFilterFn()?.name || "contains",
      }))
    );
  }, [columnFilters]);

  const table = useMaterialReactTable({
    columns,
    data: data?.results || [],
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableClickToCopy: true,
    enableKeyboardShortcuts: true,
    enableColumnActions: true,
    enableColumnDragging: true,
    enableGlobalFilter: false,
    enableGrouping: true,
    enableHiding: true,
    enableSorting: true,
    enableColumnPinning: true,
    enableRowSelection: true,
    enableColumnResizing: true,
    enableFilterMatchHighlighting: true,
    enableDensityToggle: false,
    enableEditing: true,
    createDisplayMode: "row",
    editDisplayMode: "row",
    manualSorting: true,
    manualPagination: true,
    manualFiltering: true,
    rowCount: totalCount,
    getRowId: (row) => row.id,
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableRowOrdering: false,
    initialState: { showColumnFilters: showColumnFilters },
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onDensityChange: setDensity,
    onGlobalFilterChange: setGlobalFilter,
    onShowColumnFiltersChange: setShowColumnFilters,
    onShowGlobalFilterChange: setShowGlobalFilter,
    onSortingChange: setSorting,

    muiTopToolbarProps: { sx: { backgroundColor: "#fff", padding: "8px" }, className: "top-toolbar-wrapper__new" },
    muiTableContainerProps: { sx: { backgroundColor: "#fff" }, className: "table-container-wrapper__new" },
    muiTableFooterProps: { sx: { backgroundColor: "#fff" }, className: "table-footer-wrapper__new" },
    muiBottomToolbarProps: { sx: { backgroundColor: "#fff", padding: "8px" }, className: "bottom-toolbar-wrapper__new" },

    muiTableBodyRowProps: ({ row, table }) => ({
      onKeyDown: (event: React.KeyboardEvent<HTMLTableRowElement>) => {
        if (event.key === "Enter") {
          event.preventDefault();
          const editingRow = table.getState().editingRow;
          if (editingRow) {
            if (!rowSave[editingRow.id]) return;
            const saveHandler = table.options.onEditingRowSave;
            if (saveHandler) {
              const newedit = { ...editingRow.original, ...editingRow._valuesCache };
              saveHandler({ exitEditingMode: () => table.setEditingRow(null), row: editingRow, table, values: newedit });
            }
          }
        }
      },
      ref: (node: HTMLTableRowElement | null) => {
        if (!node) return;
        const isEditing = table.getState().editingRow?.id === row.id;
        if (isEditing) {
          const saveBtn = node.querySelector('button[aria-label="Save"]') as HTMLButtonElement | null;
          if (saveBtn) {
            const isSave = !!rowSave[row.id];
            saveBtn.disabled = !isSave;
            saveBtn.style.opacity = isSave ? "1" : "0.38";
            saveBtn.style.pointerEvents = isSave ? "auto" : "none";
            saveBtn.style.cursor = isSave ? "pointer" : "not-allowed";
          }
        }
      },
    }),

    filterFns: { customFilterFn: (row, id, filterValue) => row.getValue(id) === filterValue },

    displayColumnDefOptions: {
      "mrt-row-actions": { enableColumnActions: true, size: 110,minSize:120, muiTableHeadCellProps: { sx: { minWidth: "110px", border: "1px solid #e0e0e087" } } },
      "mrt-row-numbers": { enableColumnDragging: true, enableColumnOrdering: true, enableResizing: true, muiTableHeadCellProps: { sx: { fontSize: "1.2rem" } } },
      "mrt-row-select": { enableColumnActions: true, enableHiding: true },
    },

    muiTableHeadCellProps: {
      sx: { border: "1px solid #e0e0e087", fontStyle: "normal", fontWeight: "normal", overflow: "hidden" },
    },
    muiTablePaperProps: { elevation: 0, sx: { borderRadius: "0", border: "0" } },
    muiToolbarAlertBannerProps: isLoadingError ? { color: "error", children: "Error loading data" } : undefined,

    onCreatingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleCreateRow,
    onEditingRowCancel: () => {
      setValidationErrors({});
      setRowSave({});
    },
    onEditingRowSave: handleEditSave,
    localization: { filterCustomFilterFn: "Custom Filter Fn" } as any,

    renderTopToolbarCustomActions: ({ table }) => (
      <Box sx={{ display: "flex", gap: "6px", padding: "0px", flexWrap: "wrap" }}>
        {/* <Button startIcon={<UploadFileIcon />} onClick={handleFileUploadOpen}>Upload File</Button> */}
        <Button onClick={handleDataDownload} startIcon={<BrowserUpdatedIcon />}>Export All Data</Button>
        {/* <Button onClick={handleHistory} startIcon={<HistoryIcon />}>History</Button> */}
        <BrokerTemplateDownloadButton
          apiEndpoint="/paper-app/download-legal-entity-profit-share-template/"
          buttonText="Download Template"
          fileName="profit_share_template.csv"
        />
        <Button
          disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
          onClick={() => handleExportRows(table.getSelectedRowModel().rows)}
          startIcon={<FileDownloadIcon />}
        >
          Export Selected Rows
        </Button>
        {(table.getIsSomeRowsSelected() || table.getIsAllRowsSelected()) && (
          <Button style={{ color: "red" }} onClick={() => handleBulkDelete(table.getSelectedRowModel().rows)} startIcon={<DeleteIcon />}>
            Delete Selected ({table.getSelectedRowModel().rows.length})
          </Button>
        )}
      </Box>
    ),

    renderRowActions: ({ row, table }) => (
      <Box sx={{ display: "flex", gap: "1rem" }}>
        <Tooltip title="Edit"><IconButton onClick={() => table.setEditingRow(row)}><EditIcon /></IconButton></Tooltip>
        <Tooltip title="Delete"><IconButton color="error" onClick={() => openDeleteConfirmModal(row)}><DeleteIcon /></IconButton></Tooltip>
        <Tooltip title="Copy Row"><IconButton onClick={() => handleCopyRow(row)}><ContentCopyIcon /></IconButton></Tooltip>
        {/* <Tooltip title="History"><IconButton color="default" onClick={() => openRowHistory(row)}><HistoryIcon /></IconButton></Tooltip> */}
      </Box>
    ),

    state: {
      columnVisibility, sorting, columnFilters, pagination, isLoading,
      isSaving: isCreating || isUpdating || isDeleting,
      showAlertBanner: isLoadingError,
      showProgressBars: isFetching,
      showColumnFilters
    },
  });

  return (
    <div className="page-body">
      <Container fluid>
        <Row>
          <div>
            <div className="table-ad">
              <Accordion defaultActiveKey="0" className="table-ad__addInputs">
                <Accordion.Item eventKey="0">
                  <Accordion.Header className="accordion_button">Add New Profit Share</Accordion.Header>
                  <Accordion.Body>
                    <Form onSubmit={formCreate}>
                      <div className="form_contain">
                        <div
                          className={`flex formWrapper ${inputBackgroundColour !== "" ? "inputboxcustom" : ""}`}
                          style={{ backgroundColor: inputBackgroundColour }}
                        >
                          <Box sx={{ width: "100%", p: { xs: 1, sm: 1 }, pt: "20px !important", backgroundColor: "#ffff", borderRadius: "4px", border: "1px solid #e0e0e0" }}>
                            <Box
                              className="form_default"
                              sx={{
                                display: "flex", flexWrap: "wrap", alignItems: "flex-start",
                                justifyContent: "start", gap: 0.5,
                                "& > *": { minWidth: { xs: "100%", sm: "48%", md: "23%", lg: "15%", xl: "7.5%" } },
                              }}
                            >
                              <Autocomplete
                                options={bookOptions}
                                getOptionLabel={(o) => o.name}
                                value={selectedBook}
                                onChange={(_, newVal) => {
                                  setSelectedBook(newVal);
                                  if (touched.book) {
                                    const error = validateField("book", newVal?.id || "");
                                    setFormErrors((prev) => ({ ...prev, book: error || "" }));
                                  }
                                }}
                                onFocus={() => handleFocus("book")}
                                onBlur={() => {
                                  const error = validateField("book", selectedBook?.id || "");
                                  setFormErrors((prev) => ({ ...prev, book: error || "" }));
                                }}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                sx={{ maxWidth: { sm: "200px", xs: "100%" } }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Book *"
                                    size="small"
                                    error={Boolean(formErrors.book && touched.book)}
                                    helperText={touched.book ? formErrors.book : ""}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                      fontSize: "0.75rem",
                                      "& .MuiInputBase-root": {
                                        height: "32px",
                                        fontSize: "0.75rem",
                                        padding: "0 !important",
                                      },
                                      "& .MuiInputBase-input": {
                                        padding: "4px 6px !important",
                                        fontSize: "0.75rem",
                                      },
                                      "& .MuiAutocomplete-endAdornment": {
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                      },
                                    }}
                                  />
                                )}
                              />

                              <CompactTextField
                                label="Profit Share %"
                                value={profitSharePercentage}
                                onChange={handleProfitShareChange}
                                name="profit_share_percentage"
                                type="number"
                                error={Boolean(formErrors.profit_share_percentage && touched.profit_share_percentage)}
                                helperText={
                                  touched.profit_share_percentage && formErrors.profit_share_percentage
                                    ? formErrors.profit_share_percentage
                                    : ""
                                }
                                validateOnBlur={validateOnBlur("profit_share_percentage")}
                                slotProps={{
                                  inputLabel: { shrink: true },
                                  htmlInput: {
                                    min: 0,
                                    max: 100,
                                    step: 0.01,
                                    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                                      if (e.key === "-" || e.key === "e" || e.key === "E" || e.key === "+") {
                                        e.preventDefault();
                                      }
                                    }
                                  }
                                }}
                                onFocus={() => handleFocus("profit_share_percentage")}
                                sx={{ maxWidth: { sm: "200px", xs: "100%" } }}
                              />

                              <CompactTextField
                                label="Effective From"
                                value={effectiveFrom}
                                onChange={(value) => {
                                  setEffectiveFrom(value);
                                  if (touched.effective_from && effectiveTo && value && effectiveTo < value) {
                                    setFormErrors((prev) => ({ ...prev, effective_from: "Effective To must be >= Effective From" }));
                                  } else if (touched.effective_from) {
                                    setFormErrors((prev) => ({ ...prev, effective_from: value ? "" : "Effective From is Required" }));
                                  }
                                }}
                                name="effective_from"
                                type="date"
                                error={Boolean(formErrors.effective_from && touched.effective_from)}
                                helperText={touched.effective_from ? formErrors.effective_from : ""}
                                validateOnBlur={validateOnBlur("effective_from")}
                                slotProps={{ inputLabel: { shrink: true } }}
                                onFocus={() => handleFocus("effective_from")}
                                sx={{ maxWidth: { sm: "200px", xs: "100%" } }}
                              />

                              <CompactTextField
                                label="Effective To"
                                value={effectiveTo}
                                onChange={(value) => {
                                  setEffectiveTo(value);
                                  if (touched.effective_to && effectiveFrom && value && value < effectiveFrom) {
                                    setFormErrors((prev) => ({ ...prev, effective_to: "Effective To must be >= Effective From" }));
                                  } else if (touched.effective_to) {
                                    setFormErrors((prev) => ({ ...prev, effective_to: "" }));
                                  }
                                }}
                                name="effective_to"
                                type="date"
                                error={Boolean(formErrors.effective_to && touched.effective_to)}
                                helperText={touched.effective_to ? formErrors.effective_to : ""}
                                slotProps={{ inputLabel: { shrink: true } }}
                                onFocus={() => handleFocus("effective_to")}
                                sx={{ maxWidth: { sm: "200px", xs: "100%" } }}
                              />

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  height: "32px",
                                  mb: "0.5rem",
                                  flex: { xs: "1 1 auto", sm: "0 0 auto" },
                                  minWidth: "unset",
                                }}
                              >
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={active}
                                      onChange={(e) => setActive(e.target.checked)}
                                      size="small"
                                      sx={{
                                        color: "#b9baeb",
                                        "&.Mui-checked": { color: "#009db5" },
                                        padding: "4px",
                                      }}
                                    />
                                  }
                                  label="Active"
                                  sx={{
                                    "& .MuiFormControlLabel-label": {
                                      fontSize: "0.75rem",
                                      fontWeight: "bold",
                                      color: "#333",
                                    },
                                    margin: 0,
                                    whiteSpace: "nowrap",
                                  }}
                                />
                                <Button
                                  variant="contained"
                                  type="submit"
                                  sx={{
                                    height: "32px",
                                    minWidth: "80px",
                                    backgroundColor: "#00bcd4",
                                    textTransform: "none",
                                    fontSize: "0.75rem",
                                    "&:hover": { backgroundColor: "#008c9e" },
                                  }}
                                >
                                  Add
                                </Button>
                              </Box>
                            </Box>
                          </Box>
                        </div>
                      </div>
                    </Form>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>

              <div className="table-ad__wrapper" onContextMenu={handleContextMenu}>
                <div className="table-ad__wrapperCard">
                  <MaterialReactTable table={table} />
                  <Menu
                    open={contextMenu !== null}
                    onClose={handleClose}
                    anchorReference="anchorPosition"
                    anchorPosition={contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
                  >
                    <MenuItem onClick={handleResizing}>{showRowResizing ? "Disable Resizing" : "Enable Resizing"}</MenuItem>
                    <MenuItem onClick={handlePaste}>Paste</MenuItem>
                  </Menu>
                </div>
                <FileUpload
                  open={openFileUpload}
                  onFileUploadClose={handleFileUploadClose}
                  onFileUploadSubmit={handleFileUploadSubmit}
                  acceptedFileTypes=".csv,.json"
                />
              </div>
            </div>
          </div>
        </Row>
      </Container>
    </div>
  );
};


function useGetProfitShares(
  filterModes: { mode: string; id: string; value: unknown }[],
  pagination: { pageIndex: number; pageSize: number },
  setTotalCount: (count: number) => void,
  sorting: MRT_SortingState
) {
  return useQuery<{ results: ProfitShare[]; totalCount: number }>({
    queryKey: ["profitShares", filterModes, pagination, sorting],
    queryFn: async () => {
      let url = `${backendbaseUrl}/paper-app/legal-entity-profit-share/?page=${pagination.pageIndex + 1}&page_size=${pagination.pageSize}`;
      if (filterModes.length > 0) {
        const supportedFilters = filterModes.filter(
          (e) => e.id !== "book_name"
        );

        if (supportedFilters.length > 0) {
          url += "&" + supportedFilters.map((e) =>
            `fieldName__d39d26f3-8442-44d0-8a7f-66cfcbb2c781__${e.id}=${String(e.value)}__d39d26f3-8442-44d0-8a7f-66cfcbb2c781__${e.mode}`
          ).join("&");
        }
      }
      if (sorting.length > 0) {
        const sortId = sorting[0].id === "book_name" ? "book" : sorting[0].id;
        url += `&sortField_${sortId}=${sorting[0].desc}_sorting`;
      }
      const response = await axiosInstance.get(url);
      setTotalCount(response.data.count);
      return { results: response.data.results as ProfitShare[], totalCount: response.data.count as number };
    },
    refetchOnWindowFocus: false,
  });
}

function useCreateProfitShare() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { book: string; profit_share_percentage: number; effective_from: string; effective_to: string | null; active: boolean }) => {
      const { data } = await axiosInstance.post(`${backendbaseUrl}/paper-app/legal-entity-profit-share/`, payload);
      return data;
    },
    onSettled: () => { queryClient.invalidateQueries({ queryKey: ["profitShares"] }); },
  });
}

function useUpdateProfitShare() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { id: string; book: string; profit_share_percentage: number; effective_from: string; effective_to: string | null; active: boolean }) => {
      const { data } = await axiosInstance.put(
        `${backendbaseUrl}/paper-app/legal-entity-profit-share/${payload.id}/`,
        { book: payload.book, profit_share_percentage: payload.profit_share_percentage, effective_from: payload.effective_from, effective_to: payload.effective_to, active: payload.active }
      );
      return data;
    },
    onMutate: async (newData) => {
      await queryClient.cancelQueries({ queryKey: ["profitShares"] });
      const previous = queryClient.getQueryData(["profitShares"]);
      queryClient.setQueryData(["profitShares"], (old: any) =>
        old?.map((item: ProfitShare) => item.id === newData.id ? { ...item, ...newData } : item)
      );
      return { previous };
    },
    onError: (_err, _newData, context) => { queryClient.setQueryData(["profitShares"], context?.previous); },
    onSettled: () => { queryClient.invalidateQueries({ queryKey: ["profitShares"] }); },
  });
}

function useDeleteProfitShare() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await axiosInstance.delete(`${backendbaseUrl}/paper-app/legal-entity-profit-share/${id}/`);
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ["profitShares"] });
      const previous = queryClient.getQueryData<ProfitShare[]>(["profitShares"]);
      queryClient.setQueryData(["profitShares"], (old: ProfitShare[] | undefined) => old?.filter((item) => item.id !== id));
      return { previous };
    },
    onError: (_err, _id, context) => { if (context?.previous) queryClient.setQueryData(["profitShares"], context.previous); },
    onSettled: () => { queryClient.invalidateQueries({ queryKey: ["profitShares"] }); },
  });
}


const queryClient = new QueryClient();

export default function ProfitShareByBook() {
  return (
    <QueryClientProvider client={queryClient}>
      <ProfitShareExample />
    </QueryClientProvider>
  );
}